import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, fromEvent } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { DrillPlanContext, DrillPlanImportOptions, DrillPlanImportResult, DrillPlanPlan, DrillPlanProject } from '../models/drill-plan.models';

interface DrillPlanAuthUrlResponse {
  url: string;
}

const PeriforApiEndpoint = `${environment.PeriforEndpoint}/api`;

@Injectable({
  providedIn: 'root'
})
export class DrillPlanService {
  public readonly isConnected = signal(false);

  constructor(private http: HttpClient) {}

  getAuthUrl(): Observable<DrillPlanAuthUrlResponse> {
    return this.http.get<DrillPlanAuthUrlResponse>(
      `${PeriforApiEndpoint}/drillplan/auth/url`,
      { 
        params: { 
          frontendOrigin: window.location.origin 
        } 
      }
    );
  }

  checkAuthStatus(): Observable<boolean> {
    return this.http.get<boolean>(`${PeriforApiEndpoint}/drillplan/auth/status`);
  }

  exchangeCode(code: string): Observable<void> {
    return this.http.post<void>(`${PeriforApiEndpoint}/drillplan/auth/exchange`, { 
      code,
      frontendOrigin: window.location.origin 
    });
  }

  getContext(): Observable<DrillPlanContext> {
    return this.http.get<DrillPlanContext>(`${PeriforApiEndpoint}/drillplan/context`);
  }

  getProjects(): Observable<DrillPlanProject[]> {
    return this.http.get<DrillPlanProject[]>(`${PeriforApiEndpoint}/drillplan/projects`);
  }

  getPlans(projectId: string): Observable<DrillPlanPlan[]> {
    return this.http.get<DrillPlanPlan[]>(`${PeriforApiEndpoint}/drillplan/projects/${projectId}/plans`);
  }

  importFromPlan(planId: string, options: DrillPlanImportOptions): Observable<DrillPlanImportResult> {
    return this.http.post<DrillPlanImportResult>(`${PeriforApiEndpoint}/drillplan/plans/${planId}/import`, options);
  }

  downloadArtifacts(planId: string): Observable<{ outputFolder: string; downloaded: number; failed: number }> {
    return this.http.post<{ outputFolder: string; downloaded: number; failed: number }>(
      `${PeriforApiEndpoint}/drillplan/plans/${planId}/download-artifacts`, {}
    );
  }

  openAuthPopup(): Observable<string> {
    return new Observable<string>(observer => {
      const urlSub = this.getAuthUrl().subscribe({
        next: response => {
          console.log('DrillPlan Auth URL received:', {
            authUrl: response.url,
            windowOrigin: window.location.origin,
            callbackUrl: `${window.location.origin}/drillplan/callback`
          });
          const popup = window.open(response.url, 'drillplan-auth', 'width=600,height=700');
          if (!popup) {
            console.error('Failed to open DrillPlan auth popup—popup may be blocked');
            observer.error(new Error('Popup blocked'));
            return;
          }
          const messageSub = fromEvent<MessageEvent>(window, 'message').pipe(
            filter(e => e.origin === window.location.origin && e.data?.type === 'drillplan-auth'),
            map(e => e.data.code as string),
            take(1)
          ).subscribe({
            next: code => {
              console.log('Received DrillPlan auth code from callback');
              popup?.close();
              observer.next(code);
              observer.complete();
            },
            error: err => observer.error(err)
          });

          return () => messageSub.unsubscribe();
        },
        error: err => {
          console.error('Failed to get DrillPlan auth URL:', err);
          observer.error(err);
        }
      });

      return () => urlSub.unsubscribe();
    });
  }
}
